import { z } from 'zod';
import type { OrgMember } from '@/features/organizations/services/schemas';

export const roleEnumSchema = z.enum(['read', 'write', 'full']);
export type RoleEnum = z.infer<typeof roleEnumSchema>;

export const authorizedRoleSchema = z.enum(['read', 'write', 'full']).nullable();
export type AuthorizedRole = z.infer<typeof authorizedRoleSchema>;

export const supplyPointTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type SupplyPointType = z.infer<typeof supplyPointTypeSchema>;

export const systemPartnersSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type SystemPartners = z.infer<typeof systemPartnersSchema>;

export const supplyPointSchema = z.object({
  id: z.number(),
  name: z.string(),
  partners: z.array(systemPartnersSchema),
  systemType: z.number(),
  latitude: z.string().nullable().optional(),
  longitude: z.string().nullable().optional(),
  altitude: z.string().nullable().optional(),
  street: z.string(),
  city: z.string(),
  zipCode: z.string(),
  country: z.string(),
  authorizedRole: authorizedRoleSchema,
});
export type SupplyPoint = z.infer<typeof supplyPointSchema>;

export const createSupplyPointRequestSchema = z.object({
  name: z.string().max(255),
  systemType: z.number(),
  latitude: z.string().nullable().optional(),
  longitude: z.string().nullable().optional(),
  altitude: z.string().nullable().optional(),
  street: z.string().max(255),
  city: z.string().max(100),
  zipCode: z.string().max(20),
  country: z.string(),
});
export type CreateSupplyPointRequest = z.infer<typeof createSupplyPointRequestSchema>;

export const updateSupplyPointRequestSchema = createSupplyPointRequestSchema.partial();
export type UpdateSupplyPointRequest = z.infer<typeof updateSupplyPointRequestSchema>;

export const accessStatusSchema = z.enum(['Active', 'Pending']);
export type AccessStatus = z.infer<typeof accessStatusSchema>;

export const accessSchema = z.object({
  id: z.number(),
  system: z.number(),
  user: z.object({
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
  }),
  role: roleEnumSchema,
});
export type Access = z.infer<typeof accessSchema>;

export const createAccessRequestSchema = z.object({
  user: z.email(),
  role: roleEnumSchema,
});
export type CreateAccessRequest = z.infer<typeof createAccessRequestSchema>;

export const updateAccessRequestSchema = createAccessRequestSchema.partial();
export type UpdateAccessRequest = z.infer<typeof updateAccessRequestSchema>;

// ─── Org access (derived — not a direct API schema) ───────────────────────────

/**
 * Represents an organization's access to a supply point, including its members.
 * Derived from SupplyPoint.partners + organizationQueries.membersList().
 * `membersVisible` is true when the current user's accessible orgs include this org.
 */
export type OrgWithMembers = {
  id: number;
  name: string;
  membersVisible: boolean;
  members: OrgMember[];
};
